import { useMemo } from 'react';

import { Route, Switch } from 'react-router-dom';

import { Navbar } from 'components/Navbar';
import { PrivateRoute } from 'components/PrivateRoute';
import { Submitted } from 'components/Submitted';
import { TimeoutController } from 'components/TimeoutModal/TimeoutController';
import { CandidateProvider } from 'contexts';
import { useUserContext } from 'hooks/useUserContext';
import { CandidatePage } from 'pages/candidate/CandidatePage';
import { About } from 'pages/dashboard/About';
import { EmployerTable } from 'pages/dashboard/EmployerTable';
import { Help } from 'pages/dashboard/Help';
import { NotFound } from 'pages/error/NotFound';
import { PermissionDenied } from 'pages/error/PermissionDenied';
import { AccessDenied } from 'pages/front/AccessDenied';
import { UserProps } from 'types';

const createAuthChecks = (user: UserProps | null) => [
  {
    check: () => user?.role === 'employer',
    path: '/home/employer_dashboard/403',
  },
  {
    check: () => user?.organization !== null,
    path: '/home/employer_dashboard/org_error',
  },
];

export const ContentWrapper = () => {
  const { user, loaded } = useUserContext();
  const authChecks = useMemo(() => createAuthChecks(user), [user]);

  // This stops the auth checks in each route from firing before the user data is loaded
  if (!user && !loaded) {
    // Still loading
    return null;
  }

  return (
    <CandidateProvider>
      <Submitted />
      <Navbar
        profile={false}
        links={[
          { path: '/home/employer_dashboard/candidates', label: 'Candidates' },
          { path: '/home/employer_dashboard/about', label: 'About' },
          { path: '/home/employer_dashboard/help', label: 'Help' },
        ]}
      />
      <Switch>
        <PrivateRoute
          path='/home/employer_dashboard/candidate/:candidate_id'
          component={CandidatePage}
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/employer_dashboard/candidates'
          component={EmployerTable}
          authChecks={authChecks}
        />
        <Route path='/home/employer_dashboard/about' component={About} />
        <Route path='/home/employer_dashboard/help' component={Help} />
        <Route
          path='/home/employer_dashboard/403'
          component={PermissionDenied}
        />
        <Route
          path='/home/employer_dashboard/org_error'
          render={() => (
            <AccessDenied message='Your org was assigned in error' />
          )}
        />
        <Route component={NotFound} />
      </Switch>
      <TimeoutController />
    </CandidateProvider>
  );
};
